import React, { useMemo, useState } from 'react';
import { View, Pressable, StyleSheet, Platform, TouchableOpacity, Linking } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Ionicons from '@expo/vector-icons/Ionicons';
import Toast from 'react-native-toast-message';
import {
  PageContainer,
  ContentContainer,
  Text,
  Card,
  Button,
  InstallAppPrompt,
} from '../../components/ui';
import { Theme } from '../../utils/theme';
import { useSubscription } from '../../hooks/useSubscription';
import { 
  openSubscriptionManagement, 
  getSubscriptionSourceInfo,
  showFallbackInstructions 
} from '../../utils/subscriptionUtils';
import type { ProfileStackParamList } from '../../navigation/ProfileStackNavigator';
import type { RootStackParamList } from '../../navigation/RootNavigator';
import type { NavigationProp, CompositeNavigationProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

type SettingsScreenNavigationProp = CompositeNavigationProp<
  NavigationProp<ProfileStackParamList>,
  NativeStackNavigationProp<RootStackParamList>
>;

const TERMS_URL = 'https://www.mentorist.app/termsandconditions/';
const PRIVACY_URL = 'https://www.mentorist.app/privacy-policy/';

const SettingsScreen: React.FC = () => {
  const navigation = useNavigation<SettingsScreenNavigationProp>();
  const { tier, tierDisplayName, expiryText, source, managementURL } = useSubscription();
  const [isHandlingManagement, setIsHandlingManagement] = useState(false);
  const isWeb = Platform.select({ web: true, default: false }) === true;
  const [showInstallPrompt, setShowInstallPrompt] = useState(isWeb);

  const isSubscribed = tier !== 'free';
  const isHighestTier = tier === 'ai_premium';
  const sourceInfo = useMemo(() => getSubscriptionSourceInfo(source), [source]);
  
  // Only show native management button if user is on same platform as purchase
  const canManageNatively = sourceInfo.canOpenSettings;

  // Navigate to Paywall on the root stack (ProfileStack -> MainTab -> RootStack)
  const goToPaywall = () => {
    const parent = navigation.getParent();
    const rootNav = (parent as any)?.getParent?.() || parent;
    (rootNav as any)?.navigate?.('Paywall');
  };

  const handleManageSubscription = async () => {
    if (isHandlingManagement) return;
    setIsHandlingManagement(true);

    try {
      const result = await openSubscriptionManagement(source, managementURL);
      
      if (result.shouldContactSupport) {
        // Navigate to ContactUs screen
        navigation.navigate('ContactUs', {
          category: 'support',
          subject: 'Manage Subscription Request',
        });
      } else if (!result.success && result.error) {
        // Show error toast and fallback instructions
        Toast.show({
          type: 'error',
          text1: 'Unable to open settings',
          text2: result.error,
        });
        showFallbackInstructions(source);
      }
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'Error',
        text2: 'Failed to open subscription management',
      });
    } finally {
      setIsHandlingManagement(false);
    }
  };

  const openLink = async (url: string) => {
    try {
      const supported = await Linking.canOpenURL(url);
      if (supported) {
        await Linking.openURL(url);
      }
    } catch (error) {
      console.error('[SettingsScreen] Failed to open link:', error);
      Toast.show({
        type: 'error',
        text1: 'Error',
        text2: 'Unable to open link',
      });
    }
  };

  if (isWeb) {
    return (
      <PageContainer>
        <InstallAppPrompt
          visible={showInstallPrompt}
          onDismiss={() => {
            setShowInstallPrompt(false);
            navigation.goBack();
          }}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentContainer>
        {/* Header */}
        <View style={styles.headerRow}>
          <Pressable
            onPress={() => navigation.goBack()}
            style={styles.backButton}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Ionicons name="arrow-back" size={22} color={Theme.colors.textPrimary} />
          </Pressable>
          <Text variant="h1" style={styles.header}>Subscription</Text>
          <View style={{ width: 32 }} />
        </View>

        {/* NOT SUBSCRIBED - Simple call to action */}
        {!isSubscribed && (
          <View style={styles.contentWrapper}>
            <View style={styles.emptyStateContainer}>
              <View style={styles.iconContainer}>
                <Ionicons name="star" size={48} color={Theme.colors.accent} />
              </View>
              
              <Text variant="h2" style={styles.emptyTitle}>
                Unlock Premium Features
              </Text>
              
              <Text variant="body1" color="secondary" style={styles.emptyDescription}>
                Get access to all content, AI-powered mentorship, and personalized growth recommendations.
              </Text>

              <TouchableOpacity 
                style={styles.subscribeButton} 
                onPress={goToPaywall}
                activeOpacity={0.8}
              >
                <View style={styles.subscribeButtonContent}>
                  <Ionicons name="sparkles" size={20} color="#FFFFFF" style={styles.buttonIcon} />
                  <Text style={styles.subscribeButtonText}>View Plans</Text>
                </View>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* SUBSCRIBED - Show tier info and management */}
        {isSubscribed && (
          <>
            {/* Subscription Status Card */}
            <Card style={styles.statusCard}>
              <View style={styles.statusHeader}>
                <View style={styles.tierBadge}>
                  <Ionicons 
                    name={isHighestTier ? "diamond" : "ribbon"} 
                    size={18} 
                    color="#FFFFFF" 
                    style={{ marginRight: 6 }}
                  />
                  <Text variant="body1" style={styles.tierBadgeText}>{tierDisplayName}</Text>
                </View>
              </View>

              <Text variant="h2" style={styles.subscribedTitle}>
                {isHighestTier ? 'All features unlocked' : 'You\'re subscribed'}
              </Text>

              {expiryText && (
                <Text variant="body2" color="secondary" style={styles.expiryInfo}>
                  {expiryText}
                </Text>
              )}

              {/* Upgrade button for lower tier */}
              {!isHighestTier && (
                <TouchableOpacity 
                  style={styles.upgradeButton} 
                  onPress={goToPaywall}
                  activeOpacity={0.8}
                >
                  <View style={styles.upgradeButtonContent}>
                    <Ionicons name="arrow-up-circle" size={20} color="#FFFFFF" style={styles.buttonIcon} />
                    <Text style={styles.upgradeButtonText}>Upgrade to AI Platinum</Text>
                  </View>
                </TouchableOpacity>
              )}

              {/* Manage subscription button - only show if user can manage natively */}
              {canManageNatively ? (
                <Pressable 
                  style={styles.manageButton} 
                  onPress={handleManageSubscription}
                  disabled={isHandlingManagement}
                >
                  <Text variant="body1" style={styles.manageButtonText}>
                    {isHandlingManagement ? 'Opening...' : 'Manage Subscription'}
                  </Text>
                  <Ionicons name="chevron-forward" size={18} color={Theme.colors.primary} />
                </Pressable>
              ) : (
                <Pressable 
                  style={styles.manageButton} 
                  onPress={() => {
                    // Direct to ContactUs for cross-platform scenarios
                    navigation.navigate('ContactUs', {
                      category: 'support',
                      subject: 'Manage Subscription Request',
                    });
                  }}
                  disabled={isHandlingManagement}
                >
                  <Text variant="body1" style={styles.manageButtonText}>
                    Contact Support to Manage
                  </Text>
                  <Ionicons name="chevron-forward" size={18} color={Theme.colors.primary} />
                </Pressable>
              )}

              {/* Source info - only show if we can determine the actual platform */}
              {source && source !== 'unknown' && sourceInfo.displayName !== 'Unknown' && (
                <View style={styles.sourceInfo}>
                  <Ionicons 
                    name={source === 'app_store' ? 'logo-apple' : source === 'play_store' ? 'logo-google-playstore' : 'card-outline'} 
                    size={14} 
                    color={Theme.colors.textTertiary} 
                    style={{ marginRight: 4 }}
                  />
                  <Text variant="caption" color="secondary">
                    Subscribed via {sourceInfo.displayName}
                  </Text>
                </View>
              )}
            </Card>
          </>
        )}

        {/* Legal Links */}
        <View style={styles.legalContainer}>
          <View style={styles.legalLinksRow}>
            <Pressable onPress={() => openLink(TERMS_URL)} style={styles.legalLink}>
              <Text variant="body2" style={styles.legalLinkText}>
                Terms of Service
              </Text>
            </Pressable>
            <Text variant="body2" style={styles.legalSeparator}>•</Text>
            <Pressable onPress={() => openLink(PRIVACY_URL)} style={styles.legalLink}>
              <Text variant="body2" style={styles.legalLinkText}>
                Privacy Policy
              </Text>
            </Pressable>
          </View>
        </View>
      </ContentContainer>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: Theme.spacing.xl,
  },
  backButton: {
    width: 32,
    height: 32,
    borderRadius: Theme.borderRadius.round,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Theme.colors.background,
    borderWidth: 1,
    borderColor: Theme.colors.border,
  },
  header: {
    flex: 1,
    textAlign: 'center',
  },
  
  // Not subscribed state
  contentWrapper: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: Theme.spacing.xxl,
  },
  emptyStateContainer: {
    alignItems: 'center',
    paddingHorizontal: Theme.spacing.lg,
    maxWidth: 400,
  },
  iconContainer: {
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: Theme.colors.accent + '15',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: Theme.spacing.lg,
  },
  emptyTitle: {
    textAlign: 'center',
    marginBottom: Theme.spacing.sm,
    fontWeight: '700',
  },
  emptyDescription: {
    textAlign: 'center',
    marginBottom: Theme.spacing.xl,
    lineHeight: 24,
  },
  subscribeButton: {
    backgroundColor: Theme.colors.accent,
    paddingVertical: Theme.spacing.md,
    paddingHorizontal: Theme.spacing.xl,
    borderRadius: Theme.borderRadius.lg,
    minWidth: 200,
    ...Theme.shadows.md,
  },
  subscribeButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  subscribeButtonText: {
    color: '#FFFFFF',
    fontSize: 17,
    fontWeight: '700',
    fontFamily: Theme.typography.fontFamily.bold,
  },
  buttonIcon: {
    marginRight: Theme.spacing.xs,
  },
  
  // Subscribed state
  statusCard: {
    padding: Theme.spacing.xl,
    marginBottom: Theme.spacing.lg,
  },
  statusHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: Theme.spacing.md,
  },
  tierBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Theme.colors.primary,
    paddingHorizontal: Theme.spacing.md,
    paddingVertical: Theme.spacing.xs,
    borderRadius: Theme.borderRadius.round,
  },
  tierBadgeText: {
    color: '#FFFFFF',
    fontWeight: '700',
  },
  subscribedTitle: {
    fontWeight: '700',
    marginBottom: Theme.spacing.xs,
  },
  expiryInfo: {
    marginBottom: Theme.spacing.lg,
  },
  upgradeButton: {
    backgroundColor: Theme.colors.accent,
    paddingVertical: Theme.spacing.md,
    paddingHorizontal: Theme.spacing.lg,
    borderRadius: Theme.borderRadius.lg,
    marginBottom: Theme.spacing.md,
    ...Theme.shadows.sm,
  },
  upgradeButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  upgradeButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
    fontFamily: Theme.typography.fontFamily.bold,
  },
  manageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: Theme.spacing.sm,
    paddingHorizontal: Theme.spacing.md,
    borderRadius: Theme.borderRadius.md,
    backgroundColor: Theme.colors.background,
    borderWidth: 1,
    borderColor: Theme.colors.border,
  },
  manageButtonText: {
    color: Theme.colors.primary,
    fontWeight: '600',
    marginRight: Theme.spacing.xs,
  },
  sourceInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: Theme.spacing.md,
    paddingTop: Theme.spacing.md,
    borderTopWidth: 1,
    borderTopColor: Theme.colors.border,
  },
  
  // Legal links
  legalContainer: {
    marginTop: Theme.spacing.xxl,
    marginBottom: Theme.spacing.md,
    alignItems: 'center',
  },
  legalLinksRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  legalLink: {
    paddingVertical: Theme.spacing.xs,
    paddingHorizontal: Theme.spacing.sm,
  },
  legalLinkText: {
    color: Theme.colors.primary,
    textDecorationLine: 'underline',
  },
  legalSeparator: {
    color: Theme.colors.textTertiary,
    marginHorizontal: Theme.spacing.xs,
  },
});

export default SettingsScreen;

